import { ChevronDown, ChevronUp } from "lucide-react";
import { useState } from "react";

import {
  MAP_DOMAIN_COLOR,
  MAP_DOMAIN_LABEL,
  type MapDomain,
} from "@/presentation/data/architecture-map-data";

const MAP_DOMAIN_ORDER: readonly MapDomain[] = [
  "actors",
  "supply-chain",
  "catalog-content",
  "pricing-commerce",
  "fulfillment-ops",
  "customer-experience",
  "finance",
  "app-core",
  "data-bi",
];

function DomainSwatch({ domain }: { domain: MapDomain }) {
  return (
    <div data-slot="map-legend-domain-chip" className="flex items-center gap-1.5">
      <span
        className="inline-block size-2.5 shrink-0 rounded-full"
        style={{ background: MAP_DOMAIN_COLOR[domain] }}
      />
      <span className="text-[11px] font-medium">{MAP_DOMAIN_LABEL[domain]}</span>
    </div>
  );
}

function OwnershipPill({ kind }: { kind: "yandex" | "external" }) {
  const isYandex = kind === "yandex";
  return (
    <span
      className="font-data inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[9px] font-semibold tracking-wide text-white uppercase"
      style={{
        background: isYandex ? "oklch(0.55 0.17 190)" : "oklch(0.4 0.02 240)",
      }}
    >
      <span className="size-1 rounded-full bg-white" />
      {isYandex ? "Yandex" : "External"}
    </span>
  );
}

function EdgeSwatch({ label, sampleClass }: { label: string; sampleClass: string }) {
  return (
    <div data-slot="map-legend-edge-chip" className="flex items-center gap-1.5">
      <span className={`h-0.5 w-6 shrink-0 rounded-full ${sampleClass}`} aria-hidden />
      <span className="text-[11px] font-medium">{label}</span>
    </div>
  );
}

export function ArchitectureMapLegend() {
  const [collapsed, setCollapsed] = useState(false);

  if (collapsed) {
    return (
      <button
        type="button"
        data-slot="architecture-map-legend"
        data-collapsed="true"
        onClick={() => setCollapsed(false)}
        className="pointer-events-auto flex items-center gap-1.5 rounded-lg border border-[oklch(0.91_0.008_240)] bg-white/95 px-3 py-1.5 shadow-sm backdrop-blur hover:bg-white"
        aria-label="Expand legend"
      >
        <span className="eyebrow !text-[9px]">Legend</span>
        <ChevronDown className="size-3.5 text-[oklch(0.5_0.02_240)]" aria-hidden />
      </button>
    );
  }

  return (
    <div
      data-slot="architecture-map-legend"
      data-collapsed="false"
      className="pointer-events-auto flex max-w-[min(96vw,1100px)] flex-wrap items-center gap-x-5 gap-y-2 rounded-lg border border-[oklch(0.91_0.008_240)] bg-white/95 px-4 py-2.5 shadow-sm backdrop-blur"
    >
      <div className="flex items-center gap-2">
        <span className="eyebrow !text-[9px]">Domains</span>
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
          {MAP_DOMAIN_ORDER.map((d) => (
            <DomainSwatch key={d} domain={d} />
          ))}
        </div>
      </div>
      <div className="bg-border h-4 w-px" />
      <div className="flex items-center gap-2">
        <span className="eyebrow !text-[9px]">Ownership</span>
        <OwnershipPill kind="yandex" />
        <OwnershipPill kind="external" />
      </div>
      <div className="bg-border h-4 w-px" />
      <div className="flex items-center gap-2">
        <span className="eyebrow !text-[9px]">Edges</span>
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
          <EdgeSwatch label="Primary" sampleClass="bg-[oklch(0.55_0.17_190)] h-1" />
          <EdgeSwatch
            label="Manual"
            sampleClass="h-1 border border-dashed border-[oklch(0.52_0.12_145)] bg-transparent"
          />
          <EdgeSwatch
            label="Feedback"
            sampleClass="h-1 border border-dashed border-[oklch(0.55_0.19_300)] bg-transparent"
          />
          <EdgeSwatch label="Money" sampleClass="bg-[oklch(0.58_0.14_75)] h-1" />
          <EdgeSwatch
            label="Dependency"
            sampleClass="h-1 border border-dotted border-[oklch(0.48_0.03_250)] bg-transparent"
          />
        </div>
      </div>
      <div className="bg-border h-4 w-px" />
      <span className="text-muted-foreground text-[10px]">
        Star to include / exclude per stage · click a card to spotlight arrows · drag to rearrange ·
        auto-saved
      </span>
      <button
        type="button"
        onClick={() => setCollapsed(true)}
        className="ml-auto flex size-5 shrink-0 items-center justify-center rounded text-[oklch(0.5_0.02_240)] hover:bg-[oklch(0.95_0.01_240)]"
        aria-label="Collapse legend"
      >
        <ChevronUp className="size-3.5" aria-hidden />
      </button>
    </div>
  );
}
